<script lang="ts">
import LocalizedLink from "$lib/components/LocalizedLink.svelte";
/**
 * @module routes/[[locale=locale]]/cv/ats/+page
 * @description ATS (Applicant Tracking System) optimized version of the CV page.
 * Renders the curriculum vitae in a plain-text, machine-readable format designed
 * to maximize compatibility with automated resume parsing systems.
 *
 * Unlike the styled CV page (`/cv`), this version:
 * - Uses simple semantic HTML without complex layouts or images
 * - Avoids masonry grids, skill level bars, and timeline cards
 * - Formats dates as `MM/YYYY` via the `formatDate()` helper
 * - Lists skills inline as comma-separated text grouped by category
 * - Strips bullet point characters and normalizes whitespace in descriptions
 * - Uses scoped CSS with plain fonts, borders, and minimal styling
 *
 * Data sources:
 * - `/api/cv` provides translated `education`, `experience`, and `skills` arrays
 * - `$lib/contact` provides contact detail constants
 *
 * Includes scoped CSS providing ATS-friendly typography and layout.
 *
 * @see {@link routes/[[locale=locale]]/cv/+page.svelte} for the visually styled CV
 * @see {@link routes/[[locale=locale]]/cv/+layout.svelte} for the shared CV layout
 */

import { contactDetails } from "$lib/contact";
import { t, locale } from "$lib/i18n";
import { cn } from "$lib/utils";
import { LoaderCircle as Loader2 } from "@lucide/svelte";

// --- Types matching /api/cv response ---

type DurationKey = {
  type: string;
  count: number;
};

type EducationItem = {
  key: string;
  image?: string;
  imgAlt: string;
  imageFallback: string;
  name: string;
  startdate: string;
  enddate?: string;
  description: string;
  badges?: string[];
  darkImage?: string;
};

type ExperienceItem = {
  key: string;
  image?: string;
  imgAlt: string;
  imageFallback: string;
  name: string;
  startdate: string;
  enddate?: string;
  description: string;
  badges?: string[];
  darkImage?: string;
};

type SkillItem = {
  title: string;
  category: string;
  badgeKeys: string[];
  staticBadges?: string[];
  translatedBadges?: string[];
  image: string;
  imageAlt: string;
  imageFallback: string;
  level: number;
  darkImage?: string;
  experience?: DurationKey;
};

type SkillCategoryResponse = {
  key: string;
  title: string;
  skills: SkillItem[];
};

type CVResponse = {
  education: EducationItem[];
  experience: ExperienceItem[];
  skills: SkillCategoryResponse[];
};

// --- State ---

let { data } = $props();
let cvData = $derived(
  data?.cvData
    ? (data.cvData as unknown as CVResponse)
    : { education: [], experience: [], skills: [] },
);

// --- Helpers ---

function formatDate(d?: string): string {
  if (!d) return "";
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString([$locale], {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}
</script>

<svelte:head>
  <title>{$t("sites.cv")} | {$t("layout.title")}</title>
</svelte:head>

  <div class={cn("ats-cv")}>
    <div>
      <div class={cn("text-center pb-2")}>
        <h1 class={cn("font-bold")}>{$t("aboutme.name")}</h1>
        <p class={cn("subtitle text-muted-foreground")}>
          {$t("cv.about.title")}
        </p>
      </div>
      <div>
        <div
          class={cn(
            "justify-center grid grid-cols-1 md:grid-cols-2 gap-6 text-base contact-info",
          )}
        >
          <!-- Contact Info -->
          <div class={cn("flex flex-col gap-2")}>
            <LocalizedLink
              href={contactDetails.email.link}
              class={cn("flex items-center gap-2 ")}
            >
              Email: {contactDetails.email.display}
            </LocalizedLink>
            <LocalizedLink
              href={contactDetails.telephone.link}
              class={cn("flex items-center gap-2")}
            >
              {$t("contact.mobilephone")}: {contactDetails.telephone.display}
            </LocalizedLink>
            <div class={cn("flex items-center gap-2 text-muted-foreground")}>
              {$t("cv.about.birthday")}: {new Date(
                "2003-06-06",
              ).toLocaleDateString([$locale], {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
            </div>
          </div>
          <!-- Address -->
          <div class={cn("flex flex-col gap-2")}>
            <LocalizedLink
              href={contactDetails.address.link}
              class={cn("flex items-start gap-2")}
            >
              {$t("contact.address")}:
              <span>
                {contactDetails.address.street}<br />
                {contactDetails.address.zip}
                {$t("cv.about.address.berlin")}, {$t(
                  "cv.about.address.germany",
                )}
              </span>
            </LocalizedLink>
          </div>
      </div>
    </div>
  </div>

    <!-- Education -->
    <h2>{$t("cv.education.title")}</h2>
    {#each (cvData?.education || []) as edu}
      {@const dateRange = `${formatDate(edu.startdate)} – ${formatDate(edu.enddate)}`}
      {@const description = edu.description?.replace(/[•]/g, "-") || ""}
      {@const badges = (edu.badges || [])
        .filter((b) => b && b.trim() !== "")
        .join(", ")}

      <div class={cn("entry")}>
        <div class={cn("entry-header")}>
          <span class={cn("entry-title")}>{edu.name}</span>
          <span class={cn("entry-date")}>{dateRange}</span>
        </div>
        {#if badges}
          <p class={cn("entry-badges")}><strong>Keywords:</strong> {badges}</p>
        {/if}
        <p class={cn("entry-description")}>{description}</p>
      </div>
    {/each}

    <!-- Experience -->
    <h2>{$t("cv.experience.title")}</h2>
    {#each (cvData?.experience || []) as exp}
      {@const dateRange = `${formatDate(exp.startdate)} – ${formatDate(exp.enddate)}`}
      {@const description =
        exp.description?.replace(/[•]/g, "").replace(/\n/g, " ").trim() || ""}
      {@const badges = (exp.badges || [])
        .filter((b) => b && b.trim() !== "")
        .join(", ")}

      <div class={cn("entry")}>
        <div class={cn("entry-header")}>
          <span class={cn("entry-title")}>{exp.name}</span>
          <span class={cn("entry-date")}>{dateRange}</span>
        </div>
        {#if badges}
          <p class={cn("entry-badges")}><strong>Keywords:</strong> {badges}</p>
        {/if}
        <p class={cn("entry-description")}>{description}</p>
      </div>
    {/each}

    <!-- Skills -->
    <h2>{$t("cv.skills.title")}</h2>
    {#each (cvData?.skills || []) as cat}
      {@const catSkills = (cat.skills || [])
        .map((s) => {
          const badgesStr =
            s.translatedBadges && s.translatedBadges.length > 0
              ? ` (${s.translatedBadges.join(", ")})`
              : "";
          return `${s.title}${badgesStr}`;
        })
        .filter((s) => s && s.trim() !== "")
        .join(", ")}

      {#if catSkills}
        <div class={cn("skills-category")}>
          <span class={cn("skills-title")}>{cat.title}: </span>
          <span class={cn("skills-list")}>{catSkills}</span>
        </div>
      {/if}
    {/each}
  </div>


<style>
  .ats-cv h1 {
    font-size: 18pt;
    margin: 0 0 4px 0;
    text-align: center;
  }
  .ats-cv .subtitle {
    font-size: 12pt;
    text-align: center;
    margin-bottom: 12px;
  }
  .ats-cv .contact-info {
    text-align: left;
    margin-bottom: 16px;
    font-size: 10pt;
  }
  .ats-cv .contact-info span {
    margin: 0 8px;
  }
  .ats-cv h2 {
    font-size: 12pt;
    border-bottom: 1px solid var(--color-border);
    padding-bottom: 4px;
    margin: 16px 0 8px 0;
    text-transform: uppercase;
  }
  .ats-cv .entry {
    margin-bottom: 12px;
  }
  .ats-cv .entry-header {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    margin-bottom: 2px;
  }
  .ats-cv .entry-title {
    font-weight: bold;
  }
  .ats-cv .entry-date {
    font-weight: normal;
    white-space: nowrap;
  }
  .ats-cv .entry-description {
    margin: 0;
  }
  .ats-cv .skills-category {
    margin-bottom: 8px;
  }
  .ats-cv .skills-title {
    font-weight: bold;
    display: inline;
  }
  .ats-cv .skills-list {
    display: inline;
  }
  .ats-cv .entry-badges {
    margin: 4px 0 0 0;
    font-size: 0.9em;
    color: var(--color-muted-foreground);
  }
</style>
